import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, ChefHat, ShoppingCart, Clock, DollarSign, RefreshCw, PlusCircle, Check, ChevronLeft, ChevronRight, Star, Flame, Beef, Wheat, Droplet, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'];

const dietaryOptions = ['Vegetarian', 'Vegan', 'Keto', 'Paleo', 'Gluten-Free', 'Dairy-Free', 'Nut-Free', 'Low-Carb', 'Mediterranean', 'Pescatarian'];
const cuisineOptions = ['Italian', 'Asian', 'Mexican', 'Mediterranean', 'Indian', 'American', 'Thai', 'Japanese', 'French', 'Middle Eastern'];

const regionOptions = [
  { key: 'texas', label: 'Texas', stores: 'H-E-B, Kroger, Walmart' },
  { key: 'california', label: 'California (Los Angeles)', stores: "Ralphs, Trader Joe's, Whole Foods" },
  { key: 'florida', label: 'Florida', stores: 'Publix, Winn-Dixie, Walmart' }
];

function MealCard({ meal, mealType, dayKey, planId, token, onSwap, onLog }) {
  const { t } = useTranslation();
  const [swapping, setSwapping] = useState(false);
  const [logging, setLogging] = useState(false);
  const [logged, setLogged] = useState(false);

  if (!meal || typeof meal !== 'object') return null;

  const handleSwap = async () => {
    setSwapping(true);
    try {
      const res = await fetch(`${API}/meal-plan/swap-meal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId, day_key: dayKey, meal_type: mealType })
      });
      if (res.ok) {
        const data = await res.json();
        if (onSwap) onSwap(dayKey, mealType, data.new_meal);
      }
    } catch (e) { /* silent */ }
    setSwapping(false);
  };

  const handleLog = async () => {
    setLogging(true);
    try {
      const res = await fetch(`${API}/meal-plan/quick-log-meal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meal_name: meal.name, meal_type: mealType,
          calories: meal.calories || 0, protein: meal.protein || 0,
          carbs: meal.carbs || 0, fats: meal.fats || 0
        })
      });
      if (res.ok) {
        setLogged(true);
        if (onLog) onLog();
      }
    } catch (e) { /* silent */ }
    setLogging(false);
  };

  const mealColors = { breakfast: 'border-amber-500/30', lunch: 'border-orange-500/30', dinner: 'border-red-500/30', snack: 'border-green-500/30' };

  return (
    <div className={`bg-gray-50 dark:bg-gray-900 dark:bg-gray-800/50 rounded-lg border ${mealColors[mealType] || 'border-gray-200 dark:border-gray-700/50'} p-3 hover:bg-gray-100 dark:hover:bg-gray-800/70 transition-colors`} data-testid={`meal-card-${dayKey}-${mealType}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{mealType}</span>
            {meal.estimated_cost && (
              <span className="text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10 px-1.5 py-0.5 rounded">{meal.estimated_cost}</span>
            )}
          </div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">{meal.name}</h4>
        </div>
        <button onClick={handleSwap} disabled={swapping} className="p-1 text-gray-400 hover:text-orange-400 transition-colors" title="Swap meal" data-testid={`swap-btn-${dayKey}-${mealType}`}>
          <RefreshCw className={`w-3.5 h-3.5 ${swapping ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-4 gap-1 text-xs mb-2">
        <div className="flex items-center gap-1 text-orange-500 dark:text-orange-300"><Flame className="w-3 h-3" />{Math.round(meal.calories || 0)}</div>
        <div className="flex items-center gap-1 text-red-500 dark:text-red-300"><Beef className="w-3 h-3" />{Math.round(meal.protein || 0)}g</div>
        <div className="flex items-center gap-1 text-yellow-600 dark:text-yellow-300"><Wheat className="w-3 h-3" />{Math.round(meal.carbs || 0)}g</div>
        <div className="flex items-center gap-1 text-blue-500 dark:text-blue-300"><Droplet className="w-3 h-3" />{Math.round(meal.fats || 0)}g</div>
      </div>

      {logged ? (
        <div className="flex items-center justify-center gap-1.5 py-1.5 bg-green-600/20 text-green-400 text-xs font-medium rounded-md" data-testid={`logged-${dayKey}-${mealType}`}>
          <Check className="w-3.5 h-3.5" /> Logged!
        </div>
      ) : (
        <button onClick={handleLog} disabled={logging} className="w-full flex items-center justify-center gap-1.5 py-1.5 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 text-xs font-medium rounded-md transition-colors" data-testid={`log-btn-${dayKey}-${mealType}`}>
          <PlusCircle className="w-3.5 h-3.5" /> {logging ? 'Logging...' : 'Quick Log'}
        </button>
      )}
    </div>
  );
}

function DayView({ dayKey, dayLabel, meals, planId, token, onSwap, onLog }) {
  if (!meals || typeof meals !== 'object') return null;
  const totalCal = MEAL_TYPES.reduce((sum, t) => sum + (meals[t]?.calories || 0), 0);

  return (
    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700/50 p-4" data-testid={`day-${dayKey}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 dark:text-white">{dayLabel}</h3>
        <span className="text-xs text-orange-400 font-medium">{Math.round(totalCal)} cal total</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {MEAL_TYPES.map(type => meals[type] && (
          <MealCard key={type} meal={meals[type]} mealType={type} dayKey={dayKey} planId={planId} token={token} onSwap={onSwap} onLog={onLog} />
        ))}
      </div>
    </div>
  );
}

export default function AIMealPlanner({ token }) {
  const [activeView, setActiveView] = useState('create');
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});
  const [selectedDay, setSelectedDay] = useState(0);
  const [planRequest, setPlanRequest] = useState({
    duration_days: 7, dietary_restrictions: [], cuisine_preferences: [], budget_level: 'medium', cooking_time: 'medium', region: 'texas'
  });

  const fetchPlans = useCallback(async () => {
    try {
      const res = await fetch(`${API}/meal-plan/ai-plans`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setPlans(data.ai_meal_plans || []);
      }
    } catch (e) { /* silent */ }
  }, [token]);

  const fetchFavorites = useCallback(async () => {
    try {
      const res = await fetch(`${API}/meal-plan/favorites`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setFavorites(data.favorites || []);
      }
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => { fetchPlans(); fetchFavorites(); }, [fetchPlans, fetchFavorites]);

  const generatePlan = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/meal-plan/create`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(planRequest)
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentPlan(data.plan);
        setActiveView('weekly');
        fetchPlans();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to generate plan');
      }
    } catch (e) { alert('Failed to generate plan'); }
    setLoading(false);
  };

  const handleSwap = (dayKey, mealType, newMeal) => {
    if (!currentPlan) return;
    setCurrentPlan(prev => ({
      ...prev,
      generated_plan: {
        ...prev.generated_plan,
        meal_plan: {
          ...prev.generated_plan.meal_plan,
          [dayKey]: { ...prev.generated_plan.meal_plan[dayKey], [mealType]: newMeal }
        }
      }
    }));
  };

  const toggleCheck = (item) => setCheckedItems(prev => ({ ...prev, [item]: !prev[item] }));

  const toggleOption = (arr, val, key) => {
    const updated = arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];
    setPlanRequest(prev => ({ ...prev, [key]: updated }));
  };

  const mealPlanData = currentPlan?.generated_plan?.meal_plan || {};
  const dayKeys = Object.keys(mealPlanData);
  const shoppingList = currentPlan?.generated_plan?.shopping_list || [];
  const prepTips = currentPlan?.generated_plan?.prep_tips || [];

  return (
    <div className="space-y-4" data-testid="meal-planner">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-500/20 rounded-xl p-4">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <ChefHat className="w-5 h-5 text-orange-400" /> {t('mealPlan.title')}
        </h2>
        <p className="text-sm text-gray-300 mt-1">Generate personalized weekly meal plans with AI, one-tap logging, and shopping lists</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {[
          { key: 'create', label: 'Create Plan', icon: Calendar },
          { key: 'weekly', label: 'Weekly View', icon: Calendar, disabled: !currentPlan },
          { key: 'shopping', label: 'Shopping List', icon: ShoppingCart, disabled: !currentPlan },
          { key: 'history', label: `My Plans (${plans.length})`, icon: ChefHat }
        ].map(tab => (
          <button key={tab.key} onClick={() => !tab.disabled && setActiveView(tab.key)}
            disabled={tab.disabled}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg whitespace-nowrap transition-colors ${
              activeView === tab.key ? 'bg-orange-500 text-white' : tab.disabled ? 'bg-gray-100 dark:bg-gray-800/30 text-gray-400 dark:text-gray-600 dark:text-gray-300 cursor-not-allowed' : 'bg-gray-100 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800'
            }`} data-testid={`tab-${tab.key}`}>
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Create Plan View */}
      {activeView === 'create' && (
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700/50 p-4 space-y-4">
          <h3 className="font-semibold text-gray-900 dark:text-white">Create New Meal Plan</h3>

          {favorites.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1"><Star className="w-3 h-3 text-yellow-400" /> Your favorites (will be included in the plan)</p>
              <div className="flex flex-wrap gap-1.5">
                {favorites.slice(0, 8).map((f, i) => (
                  <span key={i} className="px-2 py-1 bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-300 dark:border-yellow-500/20 text-yellow-700 dark:text-yellow-300 text-xs rounded-md">{f.name} ({f.count}x)</span>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Duration</label>
              <select value={planRequest.duration_days} onChange={(e) => setPlanRequest({...planRequest, duration_days: parseInt(e.target.value)})}
                className="w-full p-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm" data-testid="duration-select">
                <option value={3}>3 Days</option>
                <option value={7}>1 Week</option>
                <option value={14}>2 Weeks</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Budget</label>
              <div className="grid grid-cols-3 gap-1.5">
                {['low', 'medium', 'high'].map(l => (
                  <button key={l} onClick={() => setPlanRequest({...planRequest, budget_level: l})}
                    className={`p-2 rounded-lg text-xs capitalize ${planRequest.budget_level === l ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600'}`}>
                    <DollarSign className="inline w-3 h-3 mr-0.5" />{l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Cooking Time</label>
              <div className="grid grid-cols-3 gap-1.5">
                {[{k:'quick',l:'<30m'},{k:'medium',l:'30-60m'},{k:'elaborate',l:'60m+'}].map(o => (
                  <button key={o.k} onClick={() => setPlanRequest({...planRequest, cooking_time: o.k})}
                    className={`p-2 rounded-lg text-xs ${planRequest.cooking_time === o.k ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600'}`}>
                    <Clock className="inline w-3 h-3 mr-0.5" />{o.l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Dietary Restrictions</label>
            <div className="flex flex-wrap gap-1.5">
              {dietaryOptions.map(o => (
                <button key={o} onClick={() => toggleOption(planRequest.dietary_restrictions, o, 'dietary_restrictions')}
                  className={`px-2.5 py-1 rounded-md text-xs ${planRequest.dietary_restrictions.includes(o) ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600'}`}>
                  {o}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Cuisine Preferences</label>
            <div className="flex flex-wrap gap-1.5">
              {cuisineOptions.map(o => (
                <button key={o} onClick={() => toggleOption(planRequest.cuisine_preferences, o, 'cuisine_preferences')}
                  className={`px-2.5 py-1 rounded-md text-xs ${planRequest.cuisine_preferences.includes(o) ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600'}`}>
                  {o}
                </button>
              ))}
            </div>
          </div>

          {/* Region Selection for Cost Estimation */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5 flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-green-400" /> Your Region (for cost estimation)
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {regionOptions.map(r => (
                <button key={r.key} onClick={() => setPlanRequest({...planRequest, region: r.key})}
                  className={`p-3 rounded-lg text-left transition-all ${planRequest.region === r.key ? 'bg-green-50 dark:bg-green-500/20 border-2 border-green-500 text-gray-900 dark:text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'}`}
                  data-testid={`region-${r.key}`}>
                  <div className="font-medium text-sm">{r.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{r.stores}</div>
                </button>
              ))}
            </div>
          </div>

          <button onClick={generatePlan} disabled={loading}
            className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            data-testid="generate-plan-btn">
            {loading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Generating AI Plan...</> : <><ChefHat className="w-4 h-4" /> Generate Meal Plan</>}
          </button>
        </div>
      )}

      {/* Weekly View */}
      {activeView === 'weekly' && currentPlan && (
        <div className="space-y-3">
          {/* Day Navigation for mobile */}
          <div className="flex items-center justify-between sm:hidden">
            <button onClick={() => setSelectedDay(Math.max(0, selectedDay - 1))} disabled={selectedDay === 0} className="p-2 text-gray-400 disabled:opacity-30">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">{dayKeys[selectedDay]?.replace('_', ' ')}</span>
            <button onClick={() => setSelectedDay(Math.min(dayKeys.length - 1, selectedDay + 1))} disabled={selectedDay >= dayKeys.length - 1} className="p-2 text-gray-400 disabled:opacity-30">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile: Single Day */}
          <div className="sm:hidden">
            {dayKeys[selectedDay] && (
              <DayView dayKey={dayKeys[selectedDay]} dayLabel={DAYS_OF_WEEK[selectedDay] || dayKeys[selectedDay].replace('_', ' ')} meals={mealPlanData[dayKeys[selectedDay]]} planId={currentPlan.id} token={token} onSwap={handleSwap} onLog={fetchFavorites} />
            )}
          </div>

          {/* Desktop: All Days */}
          <div className="hidden sm:block space-y-3">
            {dayKeys.map((key, idx) => (
              <DayView key={key} dayKey={key} dayLabel={DAYS_OF_WEEK[idx] || key.replace('_', ' ')} meals={mealPlanData[key]} planId={currentPlan.id} token={token} onSwap={handleSwap} onLog={fetchFavorites} />
            ))}
          </div>

          {prepTips.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700/50 p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2"><ChefHat className="w-4 h-4 text-orange-400" /> Prep Tips</h3>
              <ul className="space-y-1">
                {prepTips.map((t, i) => <li key={i} className="text-xs text-gray-600 dark:text-gray-300 flex items-start gap-2"><span className="text-orange-400 mt-0.5">-</span>{t}</li>)}
              </ul>
            </div>
          )}

          {/* Cost Breakdown Section */}
          {currentPlan.generated_plan?.cost_breakdown && (
            <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 rounded-xl border border-green-500/30 p-4" data-testid="cost-breakdown">
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-400" /> 
                Cost Breakdown
                <span className="ml-auto text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded-full flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {currentPlan.generated_plan.cost_breakdown.region}
                </span>
              </h3>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">Daily Average</p>
                  <p className="text-lg font-bold text-green-400">{currentPlan.generated_plan.cost_breakdown.daily_average || 'N/A'}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">Total Weekly</p>
                  <p className="text-lg font-bold text-green-300">{currentPlan.generated_plan.cost_breakdown.total_weekly || 'N/A'}</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3 text-center col-span-2 sm:col-span-1">
                  <p className="text-xs text-gray-400 mb-1">Per Person/Meal</p>
                  <p className="text-lg font-bold text-white">
                    ${((parseFloat((currentPlan.generated_plan.cost_breakdown.total_weekly || '$0').replace(/[$,]/g, '')) || 0) / (currentPlan.duration_days * 4)).toFixed(2)}
                  </p>
                </div>
              </div>

              {currentPlan.generated_plan.cost_breakdown.by_category && (
                <div>
                  <p className="text-xs text-gray-400 mb-2">Cost by Category</p>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {Object.entries(currentPlan.generated_plan.cost_breakdown.by_category).map(([cat, cost]) => (
                      <div key={cat} className="bg-gray-800/40 rounded-md p-2 text-center">
                        <p className="text-xs text-gray-400 capitalize">{cat}</p>
                        <p className="text-sm font-semibold text-white">{cost}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Fallback: Simple total cost if no detailed breakdown */}
          {!currentPlan.generated_plan?.cost_breakdown && currentPlan.generated_plan?.total_estimated_cost && (
            <div className="text-center p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p className="text-sm font-medium text-green-400 flex items-center justify-center gap-2">
                <DollarSign className="w-4 h-4" />
                Estimated Weekly Cost: {currentPlan.generated_plan.total_estimated_cost}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Shopping List View */}
      {activeView === 'shopping' && currentPlan && (
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700/50 p-4" data-testid="shopping-list">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><ShoppingCart className="w-4 h-4 text-orange-400" /> Shopping List</h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">{Object.values(checkedItems).filter(Boolean).length}/{shoppingList.length} items</span>
          </div>
          {shoppingList.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No shopping list available</p>
          ) : (
            <ul className="space-y-1.5">
              {shoppingList.map((item, i) => (
                <li key={i} className={`flex items-center gap-2.5 p-2 rounded-md hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-800/50 cursor-pointer transition-colors ${checkedItems[item] ? 'opacity-50' : ''}`}
                  onClick={() => toggleCheck(item)} data-testid={`shopping-item-${i}`}>
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${checkedItems[item] ? 'bg-orange-500 border-orange-500' : 'border-gray-400 dark:border-gray-500'}`}>
                    {checkedItems[item] && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className={`text-sm ${checkedItems[item] ? 'line-through text-gray-400 dark:text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300 dark:text-gray-200'}`}>{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* History View */}
      {activeView === 'history' && (
        <div className="space-y-3">
          {plans.length === 0 ? (
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700/50 p-8 text-center">
              <ChefHat className="w-10 h-10 text-gray-400 dark:text-gray-600 dark:text-gray-300 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">No meal plans yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Generate your first AI meal plan!</p>
              <button onClick={() => setActiveView('create')} className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium" data-testid="create-first-plan-btn">Create Plan</button>
            </div>
          ) : plans.map((plan, idx) => (
            <div key={idx} className="bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700/50 p-4 flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{plan.duration_days}-Day Plan</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Created {new Date(plan.created_at).toLocaleDateString()} | Budget: {plan.budget_level} | {plan.dietary_restrictions?.length || 0} restrictions</p>
              </div>
              <button onClick={() => { setCurrentPlan(plan); setActiveView('weekly'); setCheckedItems({}); }}
                className="px-3 py-1.5 border border-orange-500 text-orange-400 rounded-lg text-xs font-medium hover:bg-orange-500/10 transition-colors" data-testid={`view-plan-${idx}`}>
                View Plan
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
